import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../store/authStore';
import {
  PWA_STARTUP_STORAGE_KEYS,
  resolvePwaStartup,
  type PwaStartupDecision,
} from '../../security/pwa/pwaStartupResolver';

function readLocalStorage(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function readSessionStorage(key: string): string | null {
  try {
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeLocalStorage(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {}
}

function removeLocalStorage(key: string): void {
  try {
    localStorage.removeItem(key);
  } catch {}
}

function isStandalonePwa(): boolean {
  const navigatorWithStandalone = window.navigator as Navigator & { standalone?: boolean };
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    navigatorWithStandalone.standalone === true
  );
}

function hasStoredAuthToken(): boolean {
  return Boolean(readLocalStorage('ac_token') || readSessionStorage('ac_token'));
}

function shouldMarkFirstRun(decision: PwaStartupDecision): boolean {
  return decision.shouldMarkFirstRunDone === true;
}

function shouldCheckAuthBeforeContinuing(decision: PwaStartupDecision): boolean {
  return decision.requiresAuthCheck === true || decision.reason === 'already-on-security-compliance';
}

function isActivationRedirect(path: string | null): path is string {
  return Boolean(path && (path === '/ativar' || path.startsWith('/ativar?')));
}

export function PwaStartupResolver() {
  const navigate = useNavigate();
  const location = useLocation();
  const didResolve = useRef(false);

  useEffect(() => {
    if (didResolve.current) return;
    didResolve.current = true;

    let cancelled = false;

    async function resolveStartup() {
      const pendingActivationToken = readLocalStorage(PWA_STARTUP_STORAGE_KEYS.pendingActivationToken);
      const decision = resolvePwaStartup({
        currentPath: `${location.pathname}${location.search}`,
        isStandalone: isStandalonePwa(),
        hasSessionToken: hasStoredAuthToken(),
        firstRunDone: readLocalStorage(PWA_STARTUP_STORAGE_KEYS.firstRunDone) === 'true',
        pendingActivationToken,
        pendingActivationCode: readLocalStorage(PWA_STARTUP_STORAGE_KEYS.pendingActivationCode),
      });

      if (shouldMarkFirstRun(decision)) {
        writeLocalStorage(PWA_STARTUP_STORAGE_KEYS.firstRunDone, 'true');
      }

      if (shouldCheckAuthBeforeContinuing(decision)) {
        try {
          await useAuthStore.getState().checkAuth();
        } catch (error) {
          console.warn('[PWA Startup] Falha ao sincronizar autenticacao inicial.', error);
        }
      }

      if (cancelled) return;

      if (decision.action === 'redirect-activation') {
        if (!isActivationRedirect(decision.redirectTo)) return;
        if (decision.consumePendingActivation) {
          removeLocalStorage(PWA_STARTUP_STORAGE_KEYS.pendingActivationToken);
          removeLocalStorage(PWA_STARTUP_STORAGE_KEYS.pendingActivationCode);
        }
        navigate(decision.redirectTo, { replace: true });
        return;
      }

      if (decision.action === 'redirect-security-compliance' && decision.redirectTo) {
        navigate(decision.redirectTo, { replace: true });
      }
    }

    void resolveStartup();

    return () => {
      cancelled = true;
    };
  }, [location.pathname, location.search, navigate]);

  return null;
}

export default PwaStartupResolver;
